import React, { createContext, useEffect, useMemo, useState } from 'react';
import authService from '../services/authService';

export interface AuthContextProps {
  user: any | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<any>;
  logout: () => void;
  updateUser: (user: any) => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

export const AuthContext = createContext<AuthContextProps>({
  user: null,
  loading: true,
  isAuthenticated: false,
  login: async () => ({ success: false }),
  logout: () => {},
  updateUser: () => {},
  hasPermission: () => false,
  hasRole: () => false,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const cachedUser = authService.getCurrentUser();
    setUser(cachedUser);
    setIsAuthenticated(authService.isAuthenticated());
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const data = await authService.login(email, password);
      if (data?.success && data?.data?.user) {
        setUser(data.data.user);
        setIsAuthenticated(true);
      }
      return data;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (updatedUser: any) => {
    authService.updateCurrentUser(updatedUser);
    setUser(updatedUser);
    setIsAuthenticated(Boolean(updatedUser));
  };

  const hasPermission = (permission: string) => {
    return user?.permissions?.includes(permission) || false;
  };

  const hasRole = (role: string) => {
    const userRoles = user?.roles || [];
    if (role === 'school_admin' && (userRoles.includes('headteacher') || userRoles.includes('deputy_headteacher'))) {
      return true;
    }
    return userRoles.includes(role);
  };

  const value = useMemo(
    () => ({ user, loading, isAuthenticated, login, logout, updateUser, hasPermission, hasRole }),
    [user, loading, isAuthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
